import { StorageTierType } from '@qdrant/qdrant-cloud-public-api/common/v1';
import { cdnStaticPath } from '@/helpers/url';

export const CLOUD_PROVIDER_MAP = {
  AWS: 'aws',
  GCP: 'gcp',
  AZURE: 'azure',
  HYBRID: 'hybrid',
} as const;

export const CLOUD_PROVIDER_NAME_MAP = new Map([
  ['aws', 'Amazon Web Services'],
  ['gcp', 'Google Cloud Platform'],
  ['azure', 'Microsoft Azure'],
  ['hybrid', 'Hybrid Cloud'],
]);

/** For space-constrained controls, beside the provider icon. */
export const CLOUD_PROVIDER_SHORT_NAME_MAP = new Map([
  ['aws', 'AWS'],
  ['gcp', 'GCP'],
  ['azure', 'Azure'],
  ['hybrid', 'Hybrid'],
]);

export const CLOUD_PROVIDERS = [
  CLOUD_PROVIDER_MAP.AWS,
  CLOUD_PROVIDER_MAP.GCP,
  CLOUD_PROVIDER_MAP.AZURE,
  CLOUD_PROVIDER_MAP.HYBRID,
] as const;

/** @deprecated In GRPC cloud provider is a string */
export type CloudProvider = (typeof CLOUD_PROVIDERS)[number];

/**
 * This is used for displaying country flags in the UI.
 * The mapping is not exhaustive and may not cover all country codes.
 * In case of missing flags, a globe emoji 🌍 is used as a fallback - if that happens, it's a sign that this list isn't complete anymore.
 */
export const COUNTRY_CODE_TO_EMOJI = new Map([
  ['US', '🇺🇸'],
  ['CA', '🇨🇦'],
  ['MX', '🇲🇽'],
  ['BR', '🇧🇷'],
  ['CL', '🇨🇱'],
  ['DE', '🇩🇪'],
  ['IE', '🇮🇪'],
  ['GB', '🇬🇧'],
  ['FR', '🇫🇷'],
  ['SE', '🇸🇪'],
  ['ES', '🇪🇸'],
  ['IT', '🇮🇹'],
  ['PL', '🇵🇱'],
  ['CH', '🇨🇭'],
  ['NO', '🇳🇴'],
  ['NL', '🇳🇱'],
  ['AT', '🇦🇹'],
  ['FI', '🇫🇮'],
  ['JP', '🇯🇵'],
  ['SG', '🇸🇬'],
  ['IN', '🇮🇳'],
  ['KR', '🇰🇷'],
  ['ID', '🇮🇩'],
  ['MY', '🇲🇾'],
  ['TH', '🇹🇭'],
  ['VN', '🇻🇳'],
  ['PH', '🇵🇭'],
  ['CN', '🇨🇳'],
  ['HK', '🇭🇰'],
  ['TW', '🇹🇼'],
  ['AU', '🇦🇺'],
  ['NZ', '🇳🇿'],
  ['AE', '🇦🇪'],
  ['IL', '🇮🇱'],
  ['SA', '🇸🇦'],
  ['QA', '🇶🇦'],
  ['ZA', '🇿🇦'],
  ['XX', '🌍'],
] as const);

type CountryCode = Parameters<typeof COUNTRY_CODE_TO_EMOJI.get>[0];
export type CountryFlag = NonNullable<ReturnType<typeof COUNTRY_CODE_TO_EMOJI.get>>;

/** The globe for a code the map lacks; nothing without a code (hybrid cloud). */
export const getCountryFlag = (countryIsoCode?: string): CountryFlag | undefined =>
  countryIsoCode
    ? (COUNTRY_CODE_TO_EMOJI.get(countryIsoCode as CountryCode) ?? COUNTRY_CODE_TO_EMOJI.get('XX'))
    : undefined;

const HYBRID_CLOUD_IMG = {
  src: cdnStaticPath('static/images/hybrid-cloud/hybrid-cloud-provider.svg'),
  themed: false,
  tooltip: 'Hybrid Cloud',
};

export const CLOUD_PROVIDER_IMGS: Record<
  string,
  {
    src: string;
    themed: boolean;
    /** @deprecated use name from the API endpoint response of /cloud-providers */
    tooltip: string;
  }
> = {
  [CLOUD_PROVIDER_MAP.AWS]: {
    src: cdnStaticPath('static/images/aws.svg'),
    themed: true,
    tooltip: 'AWS',
  },
  [CLOUD_PROVIDER_MAP.GCP]: {
    src: cdnStaticPath('static/images/gcp.svg'),
    themed: false,
    tooltip: 'Google Cloud Platform',
  },
  [CLOUD_PROVIDER_MAP.AZURE]: {
    src: cdnStaticPath('static/images/azure.svg'),
    themed: false,
    tooltip: 'Azure',
  },
  [CLOUD_PROVIDER_MAP.HYBRID]: HYBRID_CLOUD_IMG,
  /**
   * @deprecated Backward compatibility: older backups still record `private` as their provider.
   * They show the hybrid cloud logo.
   */
  private: HYBRID_CLOUD_IMG,
} as const;

type ImageSrc = { dark: string; light: string } | string;

type ImageData = (
  | {
      src: string;
      themed: true;
    }
  | {
      src: ImageSrc;
      themed: false;
    }
) & {
  tooltip: string;
};

export const K8S_DISTRIBUTION_MAP = {
  AWS: 'aws',
  AZURE: 'azure',
  CIVO: 'civo',
  DO: 'do', // Digital Ocean
  GCP: 'gcp',
  K3S: 'k3s',
  LINODE: 'linode',
  OCI: 'oci', //Oracle Cloud Infrastructure
  OPENSHIFT: 'openshift',
  OVHCLOUD: 'ovhcloud',
  SCALEWAY: 'scaleway',
  STACKIT: 'stackit',
  UNKNOWN: 'unknown',
  VULTR: 'vultr',
};

export const K8S_DISTRIBUTION_IMGS: Record<string, ImageData> = {
  [K8S_DISTRIBUTION_MAP.AWS]: {
    src: cdnStaticPath('static/images/aws.svg'),
    themed: true,
    tooltip: 'AWS',
  },
  [K8S_DISTRIBUTION_MAP.GCP]: {
    src: cdnStaticPath('static/images/gcp.svg'),
    themed: false,
    tooltip: 'Google Cloud Platform',
  },
  [K8S_DISTRIBUTION_MAP.AZURE]: {
    src: cdnStaticPath('static/images/azure.svg'),
    themed: false,
    tooltip: 'Azure',
  },
  [K8S_DISTRIBUTION_MAP.CIVO]: {
    src: cdnStaticPath('static/images/providers/civo.svg'),
    themed: false,
    tooltip: 'CIVO',
  },
  [K8S_DISTRIBUTION_MAP.DO]: {
    src: cdnStaticPath('static/images/providers/digitalocean.svg'),
    themed: false,
    tooltip: 'DigitalOcean',
  },
  [K8S_DISTRIBUTION_MAP.SCALEWAY]: {
    src: {
      light: cdnStaticPath('static/images/providers/scaleway-light.svg'),
      dark: cdnStaticPath('static/images/providers/scaleway-dark.svg'),
    },
    themed: false,
    tooltip: 'Scaleway',
  },
  [K8S_DISTRIBUTION_MAP.VULTR]: {
    src: cdnStaticPath('static/images/providers/vultr.svg'),
    themed: false,
    tooltip: 'Vultr',
  },
  [K8S_DISTRIBUTION_MAP.LINODE]: {
    src: '/images/providers/linode.svg',
    themed: false,
    tooltip: 'Linode',
  },
  [K8S_DISTRIBUTION_MAP.OPENSHIFT]: {
    src: cdnStaticPath('static/images/providers/openshift.svg'),
    themed: false,
    tooltip: 'OpenShift',
  },
  [K8S_DISTRIBUTION_MAP.OCI]: {
    src: cdnStaticPath('static/images/providers/oracle.svg'),
    themed: false,
    tooltip: 'Oracle Cloud Infrastructure',
  },
  [K8S_DISTRIBUTION_MAP.OVHCLOUD]: {
    src: {
      dark: cdnStaticPath('static/images/providers/ovh-dark.svg'),
      light: cdnStaticPath('static/images/providers/ovh-light.svg'),
    },
    themed: false,
    tooltip: 'OVH Cloud',
  },
  [K8S_DISTRIBUTION_MAP.STACKIT]: {
    src: cdnStaticPath('static/images/providers/stackit.svg'),
    themed: false,
    tooltip: 'Stackit',
  },
  [K8S_DISTRIBUTION_MAP.K3S]: {
    src: cdnStaticPath('static/images/providers/k3s.svg'),
    themed: false,
    tooltip: 'K3s',
  },
  [K8S_DISTRIBUTION_MAP.UNKNOWN]: {
    src: '',
    themed: true,
    tooltip: 'Hybrid Cloud',
  },
} as const;

export const MILLICENTS_TO_DOLLAR_OR_EURO_FACTOR = 100 * 1000;
export const AVG_DAYS_PER_MONTH = 30.5;
export const HOURS_IN_A_DAY = 24;
export const MAX_DAYS_IN_MONTH = 28;

export const QDRANT_LOG_LEVELS = ['INFO', 'TRACE', 'DEBUG', 'WARN', 'ERROR', 'OFF'] as const;
export const DEFAULT_QDRANT_LOG_LEVEL = QDRANT_LOG_LEVELS[0];

export const STORAGE_TIER_TYPES_TITLES = {
  [StorageTierType.COST_OPTIMISED]: 'Cost-Optimized',
  [StorageTierType.BALANCED]: 'Balanced',
  [StorageTierType.PERFORMANCE]: 'Performance',
  [StorageTierType.UNSPECIFIED]: '',
};

export const DEFAULT_REPLICATION_FACTOR = 2;
export const DEFAULT_MULTI_AZ_REPLICATION_FACTOR = 3;
